import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import BottomSheet from 'components/_UI/BottomSheet';
import { useCart } from 'context/restaurant/CartContext';
import { Plus, Minus, Check } from 'lucide-react';
import colors from 'theme/colors';

function trackProductFrequency(productId) {
    try {
        const key = 'waiter_product_freq';
        const freq = JSON.parse(localStorage.getItem(key) || '{}');
        freq[productId] = (freq[productId] || 0) + 1;
        localStorage.setItem(key, JSON.stringify(freq));
    } catch {}
}

function formatPrice(value) {
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function ProductDetailSheet({ product, onClose }) {
    const { addItem } = useCart();
    const [selections, setSelections] = useState({});
    const [quantity, setQuantity] = useState(1);
    const [observation, setObservation] = useState('');

    function handleSelect(group, option) {
        if (group.max_selections === 1) {
            setSelections(prev => ({
                ...prev,
                [group.id]: prev[group.id] === option.id ? undefined : option.id,
            }));
        } else {
            setSelections(prev => {
                const current = prev[group.id] || [];
                const exists = current.includes(option.id);
                if (exists) return { ...prev, [group.id]: current.filter(id => id !== option.id) };
                if (current.length >= group.max_selections) return prev;
                return { ...prev, [group.id]: [...current, option.id] };
            });
        }
    }

    function isSelected(group, option) {
        const sel = selections[group.id];
        if (!sel) return false;
        return Array.isArray(sel) ? sel.includes(option.id) : sel === option.id;
    }

    function getAdjustmentTotal() {
        let total = 0;
        product.option_groups?.forEach(group => {
            const sel = selections[group.id];
            if (!sel) return;
            const ids = Array.isArray(sel) ? sel : [sel];
            ids.forEach(id => {
                const opt = group.options.find(o => o.id === id);
                if (opt) total += Number(opt.price_adjustment);
            });
        });
        return total;
    }

    function isValid() {
        return product.option_groups?.every(group => {
            if (!group.is_required) return true;
            const sel = selections[group.id];
            if (!sel) return false;
            const count = Array.isArray(sel) ? sel.length : 1;
            return count >= (group.min_selections || 1);
        }) ?? true;
    }

    function getGroupSelectionCount(group) {
        const sel = selections[group.id];
        if (!sel) return 0;
        return Array.isArray(sel) ? sel.length : 1;
    }

    const unitPrice = Number(product.base_price) + getAdjustmentTotal();
    const totalPrice = unitPrice * quantity;
    const valid = isValid();

    function handleAdd() {
        if (!valid) return;
        addItem({ product, selections, quantity, totalPrice: unitPrice, observation: observation.trim() || null });
        trackProductFrequency(product.id);
        onClose();
    }

    return (
        <BottomSheet isOpen={true} onClose={onClose} title={product.name} size="large">
            {product.image && (
                <HeroImage
                    src={product.image.path || product.image.thumb}
                    alt={product.name}
                    onError={e => { e.target.onerror = null; e.target.src = '/assets/images/placeholder-events.png'; }}
                />
            )}

            {product.description && (
                <ProductDescription>{product.description}</ProductDescription>
            )}

            {/* Option Groups */}
            {product.option_groups?.map(group => {
                const selCount = getGroupSelectionCount(group);
                const groupComplete = group.is_required && selCount >= (group.min_selections || 1);
                return (
                    <GroupSection key={group.id}>
                        <GroupHeader>
                            <GroupHeaderLeft>
                                <GroupTitle>{group.name}</GroupTitle>
                                <GroupSub>
                                    {group.max_selections === 1
                                        ? 'Escolha 1 opção'
                                        : `Escolha até ${group.max_selections} opções`}
                                </GroupSub>
                            </GroupHeaderLeft>
                            <GroupBadge $required={group.is_required} $complete={groupComplete}>
                                {groupComplete
                                    ? <><Check size={10} /> Pronto</>
                                    : group.is_required ? 'Obrigatório' : 'Opcional'
                                }
                            </GroupBadge>
                        </GroupHeader>

                        {group.options.map(opt => {
                            const selected = isSelected(group, opt);
                            const isRadio = group.max_selections === 1;
                            return (
                                <OptionRow
                                    key={opt.id}
                                    onClick={() => handleSelect(group, opt)}
                                    $selected={selected}
                                >
                                    <OptionIndicator $selected={selected} $radio={isRadio}>
                                        {selected && <IndicatorDot $radio={isRadio} />}
                                    </OptionIndicator>
                                    <OptionName $selected={selected}>{opt.name}</OptionName>
                                    {Number(opt.price_adjustment) > 0 && (
                                        <OptionPrice>+{formatPrice(opt.price_adjustment)}</OptionPrice>
                                    )}
                                </OptionRow>
                            );
                        })}
                    </GroupSection>
                );
            })}

            <ObservationField>
                <ObservationHeader>
                    <ObservationLabel>Observação</ObservationLabel>
                    <ObservationOptional>opcional</ObservationOptional>
                </ObservationHeader>
                <ObservationInput
                    value={observation}
                    onChange={e => setObservation(e.target.value)}
                    placeholder="Ex: Sem cebola, bem passado..."
                    rows={2}
                />
            </ObservationField>

            {/* Quantity + Add button */}
            <Footer>
                <QuantityRow>
                    <QtyBtn onClick={() => setQuantity(q => Math.max(1, q - 1))} disabled={quantity <= 1}>
                        <Minus size={16} color={quantity <= 1 ? colors.nightLight : colors.occasionalPurple} />
                    </QtyBtn>
                    <QtyValue>{quantity}</QtyValue>
                    <QtyBtn onClick={() => setQuantity(q => q + 1)}>
                        <Plus size={16} color={colors.occasionalPurple} />
                    </QtyBtn>
                </QuantityRow>

                <AddBtn onClick={handleAdd} disabled={!valid}>
                    <span>Adicionar</span>
                    <AddBtnPrice>{formatPrice(totalPrice)}</AddBtnPrice>
                </AddBtn>
            </Footer>
        </BottomSheet>
    );
}

/* ───── Animations ───── */

const checkPop = keyframes`
    0% { transform: scale(0.8); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
`;

/* ───── Styled Components ───── */

const ProductDescription = styled.p`
    font-size: 14px;
    color: ${colors.nightLight};
    margin: 0 0 12px;
    line-height: 1.5;
`;

const HeroImage = styled.img`
    width: 100%;
    height: 160px;
    object-fit: cover;
    border-radius: 12px;
    margin-bottom: 16px;
`;

const GroupSection = styled.div`
    margin: 16px 0 4px;
`;

const GroupHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 2px;
    gap: 8px;
`;

const GroupHeaderLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const GroupTitle = styled.span`
    font-weight: 700;
    font-size: 15px;
    color: ${colors.nightDark};
`;

const GroupSub = styled.p`
    font-size: 12px;
    color: ${colors.nightLight};
    margin: 0 0 8px;
`;

const GroupBadge = styled.span`
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    white-space: nowrap;
    transition: all 0.2s;
    background: ${({ $complete, $required }) =>
        $complete ? `${colors.success}18` :
        $required ? `${colors.danger}18` :
        `${colors.nightLight}18`};
    color: ${({ $complete, $required }) =>
        $complete ? colors.success :
        $required ? colors.danger :
        colors.nightLight};
`;

const OptionRow = styled.div`
    display: flex;
    align-items: center;
    padding: 14px 0;
    border-bottom: 1px solid ${colors.neutralDark};
    cursor: pointer;
    gap: 12px;
    min-height: 52px;
    transition: background 0.1s;
    &:last-child { border-bottom: none; }
    &:hover { background: ${colors.neutralDark}20; }
`;

const OptionIndicator = styled.div`
    width: 22px;
    height: 22px;
    border-radius: ${({ $radio }) => $radio ? '50%' : '6px'};
    border: 2px solid ${({ $selected }) => $selected ? colors.occasionalPurple : colors.nightLight};
    background: ${({ $selected }) => $selected ? colors.occasionalPurple : 'transparent'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.15s;
`;

const IndicatorDot = styled.div`
    width: ${({ $radio }) => $radio ? '8px' : '12px'};
    height: ${({ $radio }) => $radio ? '8px' : '12px'};
    border-radius: ${({ $radio }) => $radio ? '50%' : '3px'};
    background: #fff;
    animation: ${checkPop} 0.2s ease;
`;

const OptionName = styled.span`
    flex: 1;
    font-size: 15px;
    color: ${({ $selected }) => $selected ? colors.nightDark : colors.night};
    font-weight: ${({ $selected }) => $selected ? '600' : '400'};
    transition: color 0.15s;
`;

const OptionPrice = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.occasionalPurple};
`;

const ObservationField = styled.div`
    margin: 16px 0 4px;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const ObservationHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ObservationLabel = styled.label`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const ObservationOptional = styled.span`
    font-weight: 400;
    color: ${colors.nightLight};
    font-size: 12px;
`;

const ObservationInput = styled.textarea`
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 14px;
    outline: none;
    color: ${colors.nightDark};
    background: #fff;
    resize: none;
    font-family: inherit;
    line-height: 1.5;
    transition: border-color 0.15s;
    &:focus { border-color: ${colors.occasionalPurple}; }
    &::placeholder { color: ${colors.nightLight}; }
`;

const Footer = styled.div`
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-top: 12px;
    border-top: 1px solid ${colors.neutralDark};
`;

const QuantityRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
`;

const QtyBtn = styled.button`
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 1.5px solid ${({ disabled }) => disabled ? colors.neutralDark : colors.occasionalPurple};
    background: transparent;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;
    &:hover:not(:disabled) { background: ${colors.occasionalPurple}15; }
    &:active:not(:disabled) { transform: scale(0.95); }
`;

const QtyValue = styled.span`
    font-size: 24px;
    font-weight: 700;
    color: ${colors.nightDark};
    min-width: 36px;
    text-align: center;
`;

const AddBtn = styled.button`
    width: 100%;
    padding: 16px;
    border-radius: 12px;
    background: ${({ disabled }) => disabled ? colors.neutralDark : colors.occasionalPurple};
    color: ${({ disabled }) => disabled ? colors.nightLight : '#fff'};
    font-size: 16px;
    font-weight: 700;
    border: none;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    transition: opacity 0.15s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    &:hover:not(:disabled) { opacity: 0.9; }
    &:active:not(:disabled) { transform: scale(0.98); }
`;

const AddBtnPrice = styled.span`
    opacity: 0.85;
    &::before { content: '·'; margin-right: 8px; }
`;
